import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, User, Settings, LogOut, ChevronDown, Menu, X } from 'lucide-react';
import Button from '../common/Button';
import { dashboardService } from '../../services/dashboardService';
import Logo from '../common/Logo';
const Header = ({ user, onLogout, onToggleMobileSidebar, mobileSidebarOpen = false }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const alerts = await dashboardService.getAlerts().catch(() => []);

      // Convert alerts to notifications format
      const formattedNotifications = alerts.map((alert, index) => ({
        id: index + 1,
        message: alert.message || alert.description || 'Notificare nouă',
        time: alert.timestamp ? new Date(alert.timestamp).toLocaleString('ro-RO') : 'Acum',
        read: alert.read || false,
        type: alert.type || 'info'
      }));

      setNotifications(formattedNotifications);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      // Fallback to empty array
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
      if (!event.target.closest('.notifications-container')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left side - Mobile menu + Logo */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <div className="lg:hidden">
              <Button
                variant="ghost"
                size="sm"
                icon={mobileSidebarOpen ? X : Menu}
                onClick={onToggleMobileSidebar}
                className="p-2"
              />
            </div>

            {/* Logo și titlu */}
            <div className="flex items-center space-x-3">
                <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-gray-800 leading-tight">
                  EduPro Contracts
                </h1>
                <p className="text-xs text-gray-500 hidden md:block">
                  Sistem Gestiune Contracte
                </p>
              </div>
              {/* Mobile: doar numele */}
              <h1 className="text-lg font-bold text-gray-800 sm:hidden">
                EduPro
              </h1>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Notifications */}
            <div className="relative notifications-container">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  setShowUserMenu(false);
                }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors relative"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50 max-h-96 overflow-y-auto">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900">Notificări</h3>
                      {unreadCount > 0 && (
                        <span className="text-xs text-blue-600">{unreadCount} noi</span>
                      )}
                    </div>
                  </div>

                  {loading ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      Se încarcă...
                    </div>
                  ) : notifications.length > 0 ? (
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.map((notification) => (
                        <button
                          key={notification.id}
                          onClick={() => markAsRead(notification.id)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${!notification.read ? 'bg-blue-50' : ''
                            }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${notification.read ? 'bg-gray-300' : 'bg-blue-500'
                              }`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${notification.read ? 'text-gray-700' : 'text-gray-900 font-medium'
                                }`}>
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {notification.time}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-8 text-sm text-gray-500 text-center">
                      Nu aveți notificări noi
                    </div>
                  )}

                  {notifications.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-200">
                      <button
                        onClick={() => setNotifications([])}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Șterge toate
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative user-menu-container">
              <button
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                  setShowNotifications(false);
                }}
                className="flex items-center space-x-2 sm:space-x-3 p-2 rounded-md hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-700">
                    {user?.name || user?.username || 'Administrator'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.email || 'admin@example.com'}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-500 hidden sm:block" />
              </button>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-2 border-b border-gray-200 sm:hidden">
                    <p className="text-sm font-medium text-gray-700">
                      {user?.name || user?.username || 'Administrator'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.email || 'admin@example.com'}
                    </p>
                  </div>

                  <button
                    onClick={() => navigate('/settings')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2">
                    <Settings className="w-4 h-4" />
                    <span>Setări</span>
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={onLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Deconectare</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;